"use client";

import { useState, type SyntheticEvent, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { buttonVariants } from "@/components/ui/button-variants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  MapPin,
  Phone,
  Mail,
  Upload,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";

export function Contact() {
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      setFormState({
        name: "Dev User",
        email: "dev@example.com",
        phone: "123456789",
        subject: "Test Anfrage",
        message:
          "Dies ist eine Testnachricht im Entwicklungsmodus. Bitte ignorieren.",
      });
    }
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  async function onSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setStatus("idle");

    const formData = new FormData(event.currentTarget);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setStatus("success");
      (event.target as HTMLFormElement).reset();
      setFileName("");
      setFormState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      // Fire GTM event
      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "forminator_form_submit" });
      }
    } catch (error) {
      console.error(error);
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  }
  return (
    <section
      id="contact"
      className="py-24 bg-primary text-primary-foreground relative overflow-visible"
    >
      {/* Dekoratives Hintergrund-Element für mehr Tiefe */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.1),transparent)] pointer-events-none bg-green-950" />

      <div className="container px-4 md:px-6 relative z-10 mx-auto">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl font-serif">
              Kontaktiere Uns
            </h2>
            <p className="text-primary-foreground/80 text-lg">
              Geben Sie uns einfach ein paar Details zu Ihrem Gartenprojekt oder
              Ihrem Anliegen, und wir melden uns innerhalb von 24 Stunden bei
              Ihnen. Kostenlos und unverbindlich.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Adresse</h3>
                  <p className="text-primary-foreground/80">
                    Schwarzer Weg, 32760 Detmold
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white shrink-0">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Telefon</h3>
                  <p className="text-primary-foreground/80">+49 1516 4337821</p>
                  <p className="text-xs text-primary-foreground/60 mb-2">
                    Mo.-Fr. 8:00-17:00
                  </p>
                  <Link
                    href="tel:+4915164337821"
                    className={cn(
                      buttonVariants({ variant: "outline", size: "sm" }),
                      "flex items-center gap-2 w-fit bg-white/10 text-white border-white/20 hover:bg-white/20 hover:text-white",
                    )}
                  >
                    <Phone className="h-3 w-3" />
                    Anrufen
                  </Link>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white shrink-0">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Email</h3>
                  <p className="text-primary-foreground/80">info@jugarden.de</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-background rounded-xl p-6 shadow-sm border text-foreground">
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Max Mustermann"
                    required
                    value={formState.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="max@beispiel.de"
                    required
                    value={formState.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="phone"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Telefon{" "}
                    <span className="text-muted-foreground font-normal">
                      (Optional)
                    </span>
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+49 123 45678"
                    value={formState.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="subject"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Betreff{" "}
                    <span className="text-muted-foreground font-normal">
                      (Optional)
                    </span>
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    placeholder="z.B. Heckenschnitt"
                    value={formState.subject}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="message"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Nachricht
                </label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Wie können wir Ihnen helfen?"
                  className="min-h-30"
                  required
                  value={formState.message}
                  onChange={handleInputChange}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="files"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Bilder hochladen (Optional)
                </label>
                <div className="flex items-center gap-4">
                  <Input
                    id="files"
                    type="file"
                    name="files"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        setFileName(
                          `${e.target.files.length} Datei(en) ausgewählt`,
                        );
                      } else {
                        setFileName("");
                      }
                    }}
                  />
                  <label
                    htmlFor="files"
                    className={cn(
                      buttonVariants({ variant: "outline", size: "sm" }),
                      "cursor-pointer flex items-center gap-2 w-full sm:w-auto",
                    )}
                  >
                    <Upload className="h-4 w-4" />
                    Bilder auswählen
                  </label>
                  <span className="text-xs text-muted-foreground truncate max-w-50">
                    {fileName}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  name="privacy"
                  required
                  className="mt-0.5 shrink-0 cursor-pointer"
                />
                <Label
                  htmlFor="privacy"
                  className="block text-sm text-muted-foreground leading-snug font-normal cursor-pointer"
                >
                  Ich habe die{" "}
                  <Link
                    href="/datenschutz"
                    rel="nofollow"
                    className="underline hover:text-foreground transition-colors"
                  >
                    Datenschutzerklärung
                  </Link>{" "}
                  gelesen und bin einverstanden.
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full cursor-pointer"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  "Nachricht senden"
                )}
              </Button>
              <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-muted-foreground">
                <span>Kostenlos &amp; unverbindlich</span>
                <span aria-hidden="true">·</span>
                <span>Antwort innerhalb von 24 Stunden</span>
              </p>
              {status === "success" && (
                <div className="flex items-center gap-2 p-4 text-sm text-green-900 bg-green-100 rounded-md border border-green-200">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <p>
                    Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet. Wir
                    melden uns in Kürze bei Ihnen.
                  </p>
                </div>
              )}
              {status === "error" && (
                <div className="flex items-center gap-2 p-4 text-sm text-red-900 bg-red-100 rounded-md border border-red-200">
                  <AlertCircle className="h-5 w-5 shrink-0" />
                  <p>
                    Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte
                    versuchen Sie es später erneut.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
